import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { FC, Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

import { useActionBarContext } from '@/components/app/actionBar/ActionBarContext';
import { BuyerQualificationFormState } from '@/components/buyer/interfaces';
import { isHouse } from '@/components/listing/pdf/utils';
import CreateCustomerForm from '@/components/qualification/createCustomerForm/CreateCustomerForm';
import {
  Fieldset,
  FieldsetLegend,
  FieldsetLegendLevel2,
  FieldsetLevel2,
  FormWrapper,
  RequiredInformations
} from '@/components/qualification/createQualification/CreateQualification.styles';
import { includesUserError } from '@/components/qualification/createQualification/CreateQualification.utils';
import { LayoutUserError } from '@/components/qualification/customerForm/CustomerForm.styles';
import { CustomerInformation } from '@/components/state/interfaces';
import { useAuth } from '@/hooks/auth/useAuth';
import { useIsSellerSuccessRole } from '@/hooks/auth/useIsSellerSuccessRole';
import { useCustomer } from '@/hooks/customer/useCustomer';
import { IBuyerPage } from '@/pages/customer/[customerId]/qualification/buyer';
import { useForm } from '@/utils/form';
import { PAGE_VIEW_TITLE } from '@/utils/gtm';
import { fetcher } from '@/utils/http';
import { EVENT_NAME, trackEventOnMixpanel } from '@/utils/mixPanel';
import { generateButtonId } from '@/utils/tracking';
import {
  HttpMethod,
  isNotEmpty,
  Language,
  PropertyEnum
} from '@proprioo/hokkaido';
import { ErrorLayout } from '@proprioo/salatim';

import CriteriaForm from '../criteriaForm/CriteriaForm';
import { CriteriaState, ProjectState } from '../interfaces';
import OtherForm from '../otherForm/OtherForm';
import ProjectForm from '../projectForm/ProjectForm';
import {
  formIsEmpty,
  generateEmptyBuyerQualification,
  getBuyerProjectUrl,
  getBuyerSourceLabel,
  getLocationIds
} from '../utils';
import { BuyerCreateQualificationSchema } from './BuyerForm.schema';
import { useCurrentAgent } from '@/hooks/agents/useCurrentAgent';
import { isItalianAgent } from '@/utils/agent';

type BuyerFormProps = IBuyerPage & {
  id: PAGE_VIEW_TITLE;
  activeAgent?: string;
};

const BuyerForm: FC<BuyerFormProps> = ({
  id,
  activeAgent,
  locationState,
  propertyType,
  source
}) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { customer: customerLoaded, refreshCustomer } = useCustomer();
  const { updateSaveOptions } = useActionBarContext();
  const { pathname, push } = useRouter();
  const currentAgent = useCurrentAgent();
  const isCurrentAgentItalian = isItalianAgent(currentAgent);
  const isSellerSuccess = useIsSellerSuccessRole();

  const userFormRef = useRef<HTMLDivElement>(null);

  const defaultProps = generateEmptyBuyerQualification();
  const defaultCriteria = {
    ...defaultProps.criteria,
    ...(locationState && { locationState }),
    ...(propertyType && {
      propertyTypes: {
        flat: Boolean(propertyType && propertyType === PropertyEnum.FLAT),
        house: Boolean(propertyType && isHouse(propertyType))
      }
    })
  };

  const {
    values,
    errors,
    createConfirmationModal: CreateConfirmationModal,
    resetForm,
    updateValuesFromInput
  } = useForm<BuyerQualificationFormState>({
    initialValues: {
      ...defaultProps,
      criteria: defaultCriteria,
      customer: customerLoaded.user,
      project: {
        ...defaultProps.project,
        agentOwner: (!isSellerSuccess && activeAgent) || '0',
        ...(source && {
          source: { label: t(getBuyerSourceLabel(source)), value: source }
        })
      }
    },
    onModalValidation: () => ({
      isEnabled: true,
      text: 'buyerbaseRegistrationConfirmation',
      title: 'buyerbaseRegistration'
    }),
    onValidationSuccess: async ({ updatedValues }) => {
      const { alertId, userId } = await fetcher<{
        alertId: string;
        userId: string;
      }>('/api/qualification/buyer/create', {
        body: JSON.stringify({ ...updatedValues, creator: user?.email }),
        method: HttpMethod.POST
      });

      if (customerLoaded.user.id) {
        await refreshCustomer();
      }

      trackEventOnMixpanel(EVENT_NAME.BUYER_LEAD_CREATED);

      return async () => {
        await push(getBuyerProjectUrl(userId, alertId));
      };
    },
    pageViewTitle: id,
    validationSchema: BuyerCreateQualificationSchema
  });

  const onCustomerUpdate = useCallback(
    (partialCustomer: Partial<CustomerInformation>) =>
      updateValuesFromInput({
        customer: { ...values.customer, ...partialCustomer }
      }),
    [values.customer]
  );

  const onCriteriaUpdate = useCallback(
    (partialCriteria: Partial<CriteriaState>) =>
      updateValuesFromInput({
        criteria: { ...values.criteria, ...partialCriteria }
      }),
    [values.criteria]
  );

  const onProjectUpdate = useCallback(
    (partialProject: Partial<ProjectState>) =>
      updateValuesFromInput({
        project: { ...values.project, ...partialProject }
      }),
    [values.project]
  );

  const { customer, criteria, project } = values;

  const [isPrefilled, setIsPrefilled] = useState<boolean>(
    !formIsEmpty<BuyerQualificationFormState>(
      { ...generateEmptyBuyerQualification(), criteria },
      { ...generateEmptyBuyerQualification(), criteria: defaultCriteria }
    ) &&
      Boolean(!locationState && getLocationIds(criteria.locationState).length)
  );

  useEffect(() => {
    updateSaveOptions({ saveIdButton: generateButtonId(id, 'saveAction') });
  }, []);

  // TODO consider editing generateEmptyBuyerQualification to make it create an italian base customer
  useEffect(() => {
    if (isCurrentAgentItalian) {
      defaultProps.customer.language = Language.ITALIAN;
      criteria.isMap = false;
    }
  }, [isCurrentAgentItalian, criteria.isMap]);

  return (
    <Fragment>
      {!isNotEmpty(customer.id) ? (
        <FormWrapper ref={userFormRef}>
          <CreateCustomerForm
            customer={customer}
            errors={errors}
            labels={{ email: t('emailRequired') }}
            onCustomerUpdate={onCustomerUpdate}
            onExistingCustomerSelect={async existingCustomer => {
              flushSync(() => resetForm());
              await push(`/customer/${existingCustomer.id}${pathname}`);
            }}
            // TODO fix this newsletter field for italian flow as soon as ther is instruction to
            showNewsletter={!isCurrentAgentItalian}
          />
        </FormWrapper>
      ) : (
        <Fragment>
          {includesUserError(errors) && (
            <LayoutUserError ref={userFormRef}>
              <ErrorLayout dataTest="global">
                {t('invalidUserError')}
              </ErrorLayout>
            </LayoutUserError>
          )}
        </Fragment>
      )}
      <Fieldset>
        <FieldsetLegend>{t('newBuyerProject')}</FieldsetLegend>
        <FieldsetLevel2>
          <FieldsetLegendLevel2>{t('searchCriterias')}</FieldsetLegendLevel2>
          <CriteriaForm
            criteria={criteria}
            errors={errors}
            isPrefilled={isPrefilled}
            setIsPrefilled={setIsPrefilled}
            updateCriteria={onCriteriaUpdate}
            hideMap={isCurrentAgentItalian}
            isItalian={isCurrentAgentItalian}
          />
        </FieldsetLevel2>
        <FieldsetLevel2>
          <FieldsetLegendLevel2>{t('projectDefinition')}</FieldsetLegendLevel2>
          <ProjectForm
            errors={errors}
            project={project}
            updateProject={onProjectUpdate}
          />
        </FieldsetLevel2>
        <FieldsetLevel2>
          <FieldsetLegendLevel2>{t('otherInformation')}</FieldsetLegendLevel2>
          <OtherForm
            errors={errors}
            project={project}
            reallocateActivities={false}
            updateProject={onProjectUpdate}
          />
        </FieldsetLevel2>
      </Fieldset>
      <RequiredInformations>{t('requiredInputs')}</RequiredInformations>
      <CreateConfirmationModal />
    </Fragment>
  );
};

export default BuyerForm;
